import { useEffect, useId, useMemo, useState } from 'react';
import styled from 'styled-components';
import {
  apiLoadErrorText,
  useListEconChronumLoadsInfiniteQuery,
  type ChronumLoadWithSnapshots,
  type ChronumSnapshotMeta,
} from '@shared/api';
import { Button } from '@shared/ui/Button';
import { IconChevronRight } from '@shared/ui/icons';
import { FleetChip, ScrollRegion, fleetType } from '@entities/fleet';
import {
  countsText,
  failureReason,
  feedKey,
  feedLabel,
  flattenLoads,
  formatDateTime,
  formatInt,
  loadByLabel,
  loadProgressText,
  loadStatusLabel,
  loadStatusTone,
  loadStopped,
  periodLabelLower,
  plural,
  shortHash,
  snapshotChange,
  snapshotStatusHint,
  snapshotStatusLabel,
  snapshotStatusTone,
  sortLoadSnapshots,
  sourceLabel,
  typo,
  type SnapshotChange,
} from '@entities/chronum';
import { Notice, TextButton } from '@widgets/FleetRegistry/ui';
import {
  CellMeta,
  DataTable,
  EmptyBox,
  ListSkeleton,
  NARROW,
  SectionHead,
  SectionNote,
  SectionTitle,
} from './ui';

const Loads = styled.ol`
  margin: ${({ theme }) => `${theme.spacing(4)} 0 0`};
  padding: 0;
  list-style: none;
  border-top: 1px solid ${({ theme }) => theme.colors.borderStrong};
`;

const Load = styled.li`
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};

  & > h3 {
    margin: 0;
    font: inherit;
  }
`;

const LoadButton = styled.button`
  display: grid;
  grid-template-columns: auto minmax(0, 1fr) auto;
  align-items: baseline;
  gap: ${({ theme }) => `${theme.spacing(1)} ${theme.spacing(3)}`};
  width: 100%;
  padding: ${({ theme }) => `${theme.spacing(3.5)} ${theme.spacing(2)}`};
  border: none;
  background: transparent;
  color: ${({ theme }) => theme.colors.text.primary};
  font: inherit;
  text-align: left;
  cursor: pointer;
  transition: background ${({ theme }) => theme.motion.fast} ${({ theme }) => theme.motion.easeOut};

  &:hover {
    background: ${({ theme }) => theme.colors.surface};
  }

  & > svg {
    align-self: center;
    color: ${({ theme }) => theme.colors.text.secondary};
    transition: transform ${({ theme }) => theme.motion.base} ${({ theme }) => theme.motion.easeOut};
  }

  &[aria-expanded='true'] > svg {
    transform: rotate(90deg);
  }

  & .when {
    font-size: ${fleetType.base};
    font-weight: 600;
    font-variant-numeric: tabular-nums;
  }

  & .who {
    margin-left: ${({ theme }) => theme.spacing(2)};
    font-size: ${fleetType.body};
    color: ${({ theme }) => theme.colors.text.secondary};
  }

  & .chip {
    margin-left: ${({ theme }) => theme.spacing(2)};
  }

  & .counts {
    font-size: ${fleetType.small};
    color: ${({ theme }) => theme.colors.text.secondary};
    text-align: right;
    font-variant-numeric: tabular-nums;
  }

  @container chronum (max-width: ${NARROW}) {
    grid-template-columns: auto minmax(0, 1fr);

    & .who {
      display: block;
      margin-left: 0;
    }

    & .chip {
      margin-left: 0;
    }

    & .counts {
      grid-column: 2;
      text-align: left;
    }
  }
`;

const Details = styled.div`
  padding: ${({ theme }) => `0 ${theme.spacing(2)} ${theme.spacing(4)} ${theme.spacing(8)}`};

  & > .stopped {
    margin-bottom: ${({ theme }) => theme.spacing(3)};
  }

  @container chronum (max-width: ${NARROW}) {
    padding: ${({ theme }) => `0 0 ${theme.spacing(3)}`};
  }
`;

const More = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(3)};
  margin-top: ${({ theme }) => theme.spacing(4)};
  font-size: ${fleetType.small};
  color: ${({ theme }) => theme.colors.text.secondary};
  font-variant-numeric: tabular-nums;
`;

const SnapTable = styled(DataTable)`
  td.c-hash {
    white-space: nowrap;
  }

  td.c-hash code {
    font-family: ${({ theme }) => theme.font.mono};
    font-size: ${fleetType.small};
  }

  /* «Данные изменились» — заметнее: у закрытого месяца это повод разобраться. */
  td.c-hash [data-change='changed'] {
    color: ${({ theme }) => theme.colors.text.primary};
    font-weight: 500;
  }

  td.c-note {
    min-width: 220px;
    max-width: 420px;
    color: ${({ theme }) => theme.colors.text.secondary};
    text-wrap: pretty;
  }

  @container chronum (max-width: ${NARROW}) {
    &,
    & > tbody {
      display: block;
    }

    & > thead {
      display: none;
    }

    & > tbody > tr {
      display: grid;
      grid-template-columns: minmax(0, 1fr) auto;
      gap: ${({ theme }) => `${theme.spacing(1)} ${theme.spacing(2)}`};
      padding: ${({ theme }) => `${theme.spacing(2.5)} 0`};
      border-bottom: 1px solid ${({ theme }) => theme.colors.border};
    }

    & > tbody > tr > th,
    & > tbody > tr > td {
      display: block;
      min-width: 0;
      max-width: none;
      padding: 0;
      border: none;
    }

    & > tbody > tr > td.c-hash,
    & > tbody > tr > td.c-note {
      grid-column: 1 / -1;
    }

    & > tbody > tr > td.empty {
      display: none;
    }
  }
`;

/** Как часто перечитывать историю, пока загрузка идёт. */
const POLL_MS = 3000;

const CHANGE_TEXT: Readonly<Record<SnapshotChange, string>> = {
  same: 'без изменений',
  changed: 'данные изменились',
};

function noteText(s: ChronumSnapshotMeta): string | null {
  if (s.status === 'ok') return sourceLabel(s.source);
  return snapshotStatusHint(s.status, s.message, { short: true });
}

/** Итог строки загрузки: идёт — сколько готово, иначе — итоги снимков. */
function loadCounts(load: ChronumLoadWithSnapshots): string {
  if (load.status === 'running') return `готово ${loadProgressText(load)}`;
  const counts = countsText(load.counts);
  return counts || 'снимков нет';
}

function LoadDetails({ load }: { load: ChronumLoadWithSnapshots }) {
  const snapshots = sortLoadSnapshots(load.snapshots);
  return (
    <>
      {loadStopped(load) && (
        <Notice className="stopped" $tone={load.status === 'failed' ? 'danger' : 'warning'}>
          <span className="text">
            {typo(
              `Загрузка не закончилась: готово ${loadProgressText(load)}. ${failureReason(load.message) ?? ''}`.trim(),
            )}
          </span>
        </Notice>
      )}
      {snapshots.length === 0 ? (
        <SectionNote>
          {load.status === 'running'
            ? 'Загрузка только началась — снимки появятся по мере ответов Хронума.'
            : 'Ни одного снимка эта загрузка не сохранила.'}
        </SectionNote>
      ) : (
        <ScrollRegion label="Снимки загрузки — таблица">
          <SnapTable>
            <thead>
              <tr>
                <th scope="col">Данные</th>
                <th scope="col">Итог</th>
                <th scope="col">Контрольная сумма</th>
                <th scope="col">Примечание</th>
              </tr>
            </thead>
            <tbody>
              {snapshots.map((s) => {
                const hash = shortHash(s.sha256);
                const change = snapshotChange(s);
                const note = noteText(s);
                return (
                  <tr key={s.id}>
                    <th scope="row">{feedLabel(feedKey(s.endpoint, s.basis))}</th>
                    <td>
                      <FleetChip $tone={snapshotStatusTone(s.status)}>
                        {snapshotStatusLabel(s.status)}
                      </FleetChip>
                    </td>
                    <td className={`c-hash${hash ? '' : ' empty'}`}>
                      {hash ? (
                        <>
                          <code title={s.sha256 ?? undefined}>{hash}</code>
                          {change && (
                            <CellMeta data-change={change}>{CHANGE_TEXT[change]}</CellMeta>
                          )}
                        </>
                      ) : (
                        '—'
                      )}
                    </td>
                    <td className={`c-note${note ? '' : ' empty'}`}>{note ? typo(note) : '—'}</td>
                  </tr>
                );
              })}
            </tbody>
          </SnapTable>
        </ScrollRegion>
      )}
    </>
  );
}

/** «История загрузок»: загрузки месяца со своими снимками, страницами. */
export function HistoryPanel({ period }: { period: string }) {
  const titleId = useId();
  // Пока загрузка идёт, её ход виден и здесь: список перечитывается сам.
  const [polling, setPolling] = useState(false);
  const query = useListEconChronumLoadsInfiniteQuery(period, {
    pollingInterval: polling ? POLL_MS : 0,
  });
  const pages = query.currentData?.pages;
  const loads = useMemo(() => flattenLoads(pages ?? []), [pages]);
  const total = pages?.[pages.length - 1]?.total ?? 0;
  const [toggled, setToggled] = useState<Record<string, boolean>>({});
  const anyRunning = loads.some((l) => l.status === 'running');
  useEffect(() => setPolling(anyRunning), [anyRunning]);

  if (query.isError && !pages) {
    return (
      <Notice $tone="danger" role="alert">
        <span className="text">
          {apiLoadErrorText(
            query.error,
            'Не удалось загрузить историю загрузок.',
            'Нет доступа к истории загрузок Хронума.',
          )}
        </span>
        <TextButton type="button" onClick={() => void query.refetch()}>
          Повторить
        </TextButton>
      </Notice>
    );
  }
  if (!pages) return <ListSkeleton rows={5} label="Загрузка истории" />;

  return (
    <section aria-labelledby={titleId} aria-busy={query.isFetching}>
      <SectionHead>
        <SectionTitle id={titleId}>
          Загрузки за {periodLabelLower(period)}
          {total > 0 &&
            ` · ${formatInt(total)} ${plural(total, ['загрузка', 'загрузки', 'загрузок'])}`}
        </SectionTitle>
      </SectionHead>
      <SectionNote>
        {typo(
          'Каждая загрузка сохраняет снимок ответа Хронума по каждой ручке: что пришло и когда. БДР считается по снимкам, поэтому расчёт можно повторить и проверить. Одинаковая контрольная сумма — данные с прошлой загрузки не менялись. Площадки, должности и закрытие месяцев — справочники: они снимаются в каждой загрузке, но здесь — только загрузки этого месяца.',
        )}
      </SectionNote>
      {loads.length === 0 ? (
        <EmptyBox>
          <h3>Загрузок за этот месяц ещё не было</h3>
          <p>
            {typo(
              'Данные месяца появятся после «Обновить» или загрузки по расписанию — она сама берёт текущий и прошлый месяц.',
            )}
          </p>
        </EmptyBox>
      ) : (
        <>
          <Loads>
            {loads.map((load, index) => {
              const open = toggled[load.id] ?? (index === 0 || load.status === 'running');
              const detailsId = `${titleId}-${load.id}`;
              return (
                <Load key={load.id}>
                  <h3>
                    <LoadButton
                      type="button"
                      aria-expanded={open}
                      aria-controls={detailsId}
                      onClick={() => setToggled((prev) => ({ ...prev, [load.id]: !open }))}
                    >
                      <IconChevronRight size={16} />
                      <span>
                        <span className="when">{formatDateTime(load.started_at)}</span>
                        <span className="who">{loadByLabel(load)}</span>
                        {load.status !== 'done' && (
                          <span className="chip">
                            <FleetChip $tone={loadStatusTone(load.status)}>
                              {loadStatusLabel(load.status)}
                            </FleetChip>
                          </span>
                        )}
                      </span>
                      <span className="counts">{loadCounts(load)}</span>
                    </LoadButton>
                  </h3>
                  {open && (
                    <Details id={detailsId}>
                      <LoadDetails load={load} />
                    </Details>
                  )}
                </Load>
              );
            })}
          </Loads>
          {(query.hasNextPage || loads.length < total) && (
            <More>
              <span>{`Показано ${formatInt(loads.length)} из ${formatInt(total)}.`}</span>
              {query.hasNextPage && (
                <Button
                  type="button"
                  $variant="secondary"
                  $size="sm"
                  disabled={query.isFetchingNextPage}
                  onClick={() => void query.fetchNextPage()}
                >
                  {query.isFetchingNextPage ? 'Загружаю…' : 'Показать ещё'}
                </Button>
              )}
            </More>
          )}
        </>
      )}
    </section>
  );
}
